import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'react-hot-toast';
import { Loader2, ArrowLeft, Check, AlertCircle, LockIcon, Building2, X, ChevronDown, ArrowRight, Code, Plus } from 'lucide-react';
import ContentLoader from '../../components/ui/ContentLoader';
import { getProduct, getProductIntegration, createProductIntegration, getTenantForIntegration } from '../../services/api';
import { Product, IntegrationField, Tenant } from '../../types';
import { usePermissions } from '../../hooks/usePermissions';

// Add custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

// Form data type for the integration values
interface FormData {
  [key: string]: any;
}

// MultiSelect component for handling multiple selections
interface MultiSelectProps {
  id: string;
  label: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
  error?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  label,
  options,
  value = [],
  onChange,
  required = false,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const optionsRef = useRef<HTMLDivElement[]>([]);
  const [customOption, setCustomOption] = useState<string>('');
  
  // Ensure value is always an array
  const safeValue = Array.isArray(value) ? value : [];

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
    
    // Reset focused index when opening dropdown
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  };

  const handleOptionClick = (option: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    let newValue;
    if (safeValue.includes(option)) {
      newValue = safeValue.filter(item => item !== option);
    } else {
      newValue = [...safeValue, option];
    }
    onChange(newValue);
  };

  const handleRemove = (option: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newValue = safeValue.filter(item => item !== option);
    onChange(newValue);
  };

  // Add a custom option
  const handleAddCustomOption = () => {
    const trimmedOption = customOption.trim();
    if (!trimmedOption) return;

    // Add the custom option if it's not already selected
    if (!safeValue.includes(trimmedOption)) {
      onChange([...safeValue, trimmedOption]);
    }

    // Clear the custom option input
    setCustomOption('');
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev < options.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          const option = options[focusedIndex];
          let newValue;
          if (safeValue.includes(option)) {
            newValue = safeValue.filter(item => item !== option);
          } else {
            newValue = [...safeValue, option];
          }
          onChange(newValue);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };
  
  // Scroll focused option into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && optionsRef.current[focusedIndex]) {
      optionsRef.current[focusedIndex]?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest' 
      });
    }
  }, [focusedIndex, isOpen]);
  
  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest(`#${id}-container`)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, id]);

  return (
    <div className="mb-4" id={`${id}-container`}>
      <style>{scrollbarStyles}</style>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        {/* Selected values display */}
        <div 
          id={id}
          className="w-full bg-black/70 border border-gray-800 text-gray-200 rounded-lg px-3 py-2.5 text-sm min-h-[42px] cursor-pointer flex flex-wrap gap-2 items-center focus:ring-2 focus:ring-primary-700/50 focus:outline-none"
          onClick={handleToggle}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${id}-listbox`}
        >
          {safeValue.length === 0 ? (
            <span className="text-gray-500 flex items-center">
              <span className="mr-1">Select services or add custom options...</span>
            </span>
          ) : (
            <>
              {safeValue.map(item => (
                <div key={item} className="bg-primary-900/40 border border-primary-700/30 text-primary-300 px-2 py-1 rounded-md flex items-center">
                  <span>{item}</span>
                  <button 
                    type="button" 
                    className="ml-1.5 text-primary-300/70 hover:text-primary-300 focus:outline-none"
                    onClick={(e) => handleRemove(item, e)}
                    aria-label={`Remove ${item}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </>
          )}
          <div className="ml-auto">
            <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
        
        {/* Dropdown */}
        {isOpen && (
          <div 
            id={`${id}-listbox`}
            className="absolute z-50 w-full mt-1 bg-gray-900 border border-gray-700 rounded-md shadow-lg max-h-80 overflow-y-auto custom-scrollbar"
            role="listbox"
            aria-multiselectable="true"
          >
            {/* Custom option input */}
            <div className="p-3 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Add custom option..."
                  value={customOption}
                  onChange={(e) => setCustomOption(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white placeholder-gray-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomOption();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddCustomOption}
                  className="px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
            
            {/* Predefined options */}
            {options.length === 0 ? (
              <div className="px-4 py-3 text-gray-500 text-center">No predefined options available</div>
            ) : (
              options.map((option, index) => {
                return (
                  <div 
                    key={option}
                    ref={el => {
                      if (el) {
                        // Type-safe assignment to the ref array
                        optionsRef.current[index] = el;
                      }
                    }}
                    className={`px-4 py-3 hover:bg-gray-800 transition-colors duration-150 cursor-pointer ${
                      safeValue.includes(option) ? 'bg-primary-700/30 text-primary-300 font-medium' : 'text-gray-200'
                    } ${focusedIndex === index ? 'bg-gray-800 outline-none' : ''}`}
                    onClick={(e) => handleOptionClick(option, e)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    role="option"
                    aria-selected={safeValue.includes(option)}
                    tabIndex={-1}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option}</span>
                      {safeValue.includes(option) && (
                        <Check size={16} className="text-primary-300 ml-2" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

const IntegrateProductPage: React.FC = () => {
  const { productId: routeProductId } = useParams<{ productId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { canManageIntegrations } = usePermissions();
  
  // Get organization ID from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const orgId = queryParams.get('orgId');
  
  const [product, setProduct] = useState<Product | null>(null);
  const [organization, setOrganization] = useState<Tenant | null>(null);
  const [existingIntegration, setExistingIntegration] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check permissions
  useEffect(() => {
    if (!canManageIntegrations()) {
      toast.error('You do not have permission to manage integrations');
      navigate('/integrations');
    }
  }, [canManageIntegrations, navigate]);
  
  // Use state to track multi-select values
  const [multiSelectValues, setMultiSelectValues] = useState<Record<string, string[]>>({});

  // Add these state variables at the top of the component
  const [tempMappingKey, setTempMappingKey] = useState('');
  const [tempMappingValue, setTempMappingValue] = useState('');
  const [showJsonPreview, setShowJsonPreview] = useState(false);

  // Load product and organization data
  useEffect(() => {
    const fetchData = async () => {
      if (!routeProductId) {
        setError("Product ID is missing");
        setIsLoading(false);
        return;
      }
      
      if (!orgId) {
        setError("Organization ID is missing");
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        // Fetch product details
        const productData = await getProduct(routeProductId);
        if (!productData) {
          setError("Product not found");
          setIsLoading(false);
          return;
        }
        
        setProduct(productData);
        
        // Fetch organization details
        const orgData = await getTenantForIntegration(orgId);
        if (!orgData) {
          setError("Organization not found");
          setIsLoading(false);
          return;
        }
        
        setOrganization(orgData);
        
        // Fetch existing integration if any
        const integration = await getProductIntegration(orgId, routeProductId);
        if (integration) {
          setExistingIntegration(integration);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load information");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [routeProductId, orgId]);
  
  // Dynamically build the validation schema based on product integration fields
  const getValidationSchema = (fields: IntegrationField[]) => {
    const schema: Record<string, any> = {};
    
    fields.forEach(field => {
      let fieldSchema: any = z.string();
      
      // Apply required validation if the field is required
      if (field.is_required) {
        fieldSchema = fieldSchema.min(1, `${field.label} is required`);
      } else {
        fieldSchema = fieldSchema.optional();
      }
      
      // Apply specific validation based on field type
      switch (field.type) {
        case 'number':
          fieldSchema = z.preprocess(
            (val) => Number(val),
            z.number().optional()
          );
          break;
        case 'url':
          if (field.is_required) {
            fieldSchema = z.string().url(`Please enter a valid URL for ${field.label}`);
          } else {
            fieldSchema = z.string().url(`Please enter a valid URL for ${field.label}`).optional();
          }
          break;
        case 'multi-select':
          // For multi-select, we'll validate separately
          if (field.is_required) {
            // This is just a placeholder, real validation happens in onSubmit
            fieldSchema = z.string().optional();
          } else {
            fieldSchema = z.string().optional();
          }
          break;
      }
      
      schema[field.field_key] = fieldSchema;
    });
    
    return z.object(schema);
  };
  
  // Create form with dynamic validation
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<FormData>({
    resolver: product?.integration_fields 
      ? zodResolver(getValidationSchema(product.integration_fields))
      : undefined,
    defaultValues: existingIntegration?.values || {},
  });
  
  // Update multi-select values when existing integration data is loaded
  useEffect(() => {
    if (existingIntegration?.values) {
      const newValues: Record<string, string[]> = {};
      
      // Loop through each field to find multi-select fields
      product?.integration_fields?.forEach(field => {
        if (field.type === 'multi-select' && existingIntegration.values[field.field_key]) {
          // Parse the stored values (could be string or array)
          let values: string[];
          const storedValue = existingIntegration.values[field.field_key];
          
          if (typeof storedValue === 'string') {
            // If stored as comma-separated string
            values = storedValue.split(',').map(v => v.trim()).filter(Boolean);
          } else if (Array.isArray(storedValue)) {
            // If stored as array
            values = storedValue;
          } else {
            values = [];
          }
          
          newValues[field.field_key] = values;
        }
        
        // Handle loading soar-mapping field data
        if (field.type === 'soar-mapping' && existingIntegration.values?.integration?.fieldMapping) {
          // Load field mapping data from integration object
          setValue(field.field_key, existingIntegration.values.integration.fieldMapping);
        }
        
        // For other field types, use setValue to update the form
        if (field.type !== 'multi-select' && field.type !== 'soar-mapping' && existingIntegration.values[field.field_key]) {
          setValue(field.field_key, existingIntegration.values[field.field_key]);
        }
      });
      
      setMultiSelectValues(newValues);
      
      // Integration data loaded successfully
    }
  }, [existingIntegration, product, setValue]);
  
  // Form submission handler
  const onSubmit = async (data: FormData) => {
    if (!product || !routeProductId || !orgId) return;
    
    // Check required multi-select fields
    let hasRequiredFieldErrors = false;
    
    product.integration_fields?.forEach(field => {
      if (field.type === 'multi-select' && field.is_required) {
        const values = multiSelectValues[field.field_key] || [];
        if (values.length === 0) {
          toast.error(`${field.label} is required`);
          hasRequiredFieldErrors = true;
        }
      }
    });
    
    if (hasRequiredFieldErrors) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Create a new form data object to avoid mutation
      const formData = { ...data };
      
      // Handle multi-select values specially
      Object.entries(multiSelectValues).forEach(([key, values]) => {
        // Store multi-select values as a properly formatted array
        if (Array.isArray(values) && values.length > 0) {
          formData[key] = values;
        } else if (Array.isArray(values) && values.length === 0) {
          // If empty array, send empty array to ensure it's saved properly
          formData[key] = [];
        }
      });
      
      // Ensure all multi-select fields are included, even if not in multiSelectValues
      product.integration_fields?.forEach(field => {
        if (field.type === 'multi-select' && !formData[field.field_key]) {
          formData[field.field_key] = [];
        }
      });
      
      // Create integration object if any soar-mapping fields exist
      let hasFieldMapping = false;
      let fieldMappingData = {};
      
      // Process soar-mapping fields
      product.integration_fields?.forEach(field => {
        if (field.type === 'soar-mapping' && formData[field.field_key]) {
          hasFieldMapping = true;
          fieldMappingData = {
            ...fieldMappingData,
            ...formData[field.field_key]
          };
        }
      });
      
      // Add integration data with fieldMapping if needed
      if (hasFieldMapping) {
        formData.integration = {
          type: 'soar',
          fieldMapping: fieldMappingData
        };
      }
      
      // Submit integration data
      
      // Create or update integration
      await createProductIntegration({
        customer_id: orgId,
        product_id: routeProductId,
        values: formData
      });
      
      toast.success('Integration settings saved successfully!');
      
      // Navigate back after successful save
      setTimeout(() => {
        navigate('/integrations');
      }, 1500);
    } catch (err: any) {
      console.error("Error submitting integration:", err);
      
      // Show appropriate error message
      if (err.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error('Failed to save integration settings');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render integration form fields
  const renderFormFields = () => {
    if (!product || !product.integration_fields || product.integration_fields.length === 0) {
      return (
        <div className="py-4 text-center text-text-muted-dark">
          No configuration fields available for this product.
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        {product.integration_fields.map((field, index) => {
          // Render appropriate field type
          switch (field.type) {
            case 'secret':
              return (
                <div key={field.field_key} className="mb-4">
                  <label htmlFor={field.field_key} className="block text-sm font-medium text-gray-300 mb-1.5">
                    {field.label} {field.is_required && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      id={field.field_key}
                      type="password"
                      autoComplete="off"
                      className="w-full bg-black/70 border border-gray-800 text-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-green-700/50 focus:border-green-700/50 focus:outline-none transition-all duration-200"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      {...register(field.field_key)}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockIcon className="h-4 w-4 text-gray-500" />
                    </div>
                  </div>
                  {errors[field.field_key] && (
                    <p className="mt-1 text-sm text-red-500">{errors[field.field_key]?.message as string}</p>
                  )}
                </div>
              );
            
            case 'multi-select':
              return (
                <MultiSelect 
                  key={field.field_key}
                  id={field.field_key}
                  label={field.label}
                  options={field.options || []}
                  value={multiSelectValues[field.field_key] || []}
                  onChange={(value) => {
                    setMultiSelectValues(prev => ({
                      ...prev,
                      [field.field_key]: value
                    }));
                  }}
                  required={field.is_required}
                  error={errors[field.field_key]?.message as string}
                />
              );
            
            case 'soar-mapping':
              return (
                <div key={field.field_key} className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {field.label}
                    {field.is_required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                    <div className="space-y-3">
                      {/* Display existing mappings if any */}
                      {watch(field.field_key) && Object.keys(watch(field.field_key)).length > 0 ? (
                        <div className="space-y-2">
                          {Object.entries(watch(field.field_key) || {}).map(([key, value]) => (
                            <div 
                              key={key}
                              className="flex items-center gap-2 bg-gray-800/60 p-2 rounded-md"
                            >
                              <span className="px-2 py-1 bg-blue-900/30 text-blue-400 rounded text-xs font-mono">
                                {key}
                              </span>
                              <ArrowRight size={14} className="text-gray-500" />
                              <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded text-xs font-mono">
                                {value as string}
                              </span>
                              <button 
                                type="button"
                                className="ml-auto p-1 text-gray-500 hover:text-gray-300"
                                onClick={() => {
                                  const currentMapping = watch(field.field_key) ? { ...watch(field.field_key) } : {};
                                  delete currentMapping[key];
                                  setValue(field.field_key, currentMapping);
                                }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-gray-500 text-center p-2 border border-dashed border-gray-700 rounded">
                          No field mappings added yet
                        </div>
                      )}
                      
                      {/* Form to add new mapping */}
                      <div className="pt-3 border-t border-gray-800 mt-3">
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-5">
                            <input
                              type="text"
                              placeholder="Field Key"
                              className="w-full bg-black/50 border border-gray-800 text-gray-200 rounded-lg px-3 py-2 text-sm"
                              value={tempMappingKey}
                              onChange={(e) => setTempMappingKey(e.target.value)}
                            />
                          </div>
                          <div className="col-span-5">
                            <input
                              type="text"
                              placeholder="Field Value"
                              className="w-full bg-black/50 border border-gray-800 text-gray-200 rounded-lg px-3 py-2 text-sm"
                              value={tempMappingValue}
                              onChange={(e) => setTempMappingValue(e.target.value)}
                            />
                          </div>
                          <div className="col-span-2">
                            <button
                              type="button"
                              className="w-full h-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center"
                              onClick={() => {
                                if (tempMappingKey && tempMappingValue) {
                                  const currentMapping = watch(field.field_key) ? { ...watch(field.field_key) } : {};
                                  currentMapping[tempMappingKey] = tempMappingValue;
                                  setValue(field.field_key, currentMapping);
                                  setTempMappingKey('');
                                  setTempMappingValue('');
                                }
                              }}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* JSON Preview */}
                      <div className="mt-3">
                        <div 
                          className="flex items-center justify-between cursor-pointer text-sm p-2 hover:bg-gray-800/60 rounded"
                          onClick={() => setShowJsonPreview(!showJsonPreview)}
                        >
                          <span className="text-gray-400 text-xs">
                            {showJsonPreview ? 'Hide JSON Preview' : 'Show JSON Preview'}
                          </span>
                          <Code size={14} className="text-gray-500" />
                        </div>
                        
                        {showJsonPreview && (
                          <div className="p-3 bg-black/30 border border-gray-800 rounded-md mt-2">
                            <pre className="text-xs text-gray-300 overflow-auto max-h-40">
                              {JSON.stringify(watch(field.field_key) || {}, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            
            default:
              return (
                <div key={field.field_key} className="mb-4">
                  <label htmlFor={field.field_key} className="block text-sm font-medium text-gray-300 mb-1.5">
                    {field.label} {field.is_required && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    id={field.field_key}
                    type="text"
                    className="w-full bg-black/70 border border-gray-800 text-gray-200 rounded-lg pl-3 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-green-700/50 focus:border-green-700/50 focus:outline-none transition-all duration-200"
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    {...register(field.field_key)}
                  />
                  {errors[field.field_key] && (
                    <p className="mt-1 text-sm text-red-500">{errors[field.field_key]?.message as string}</p>
                  )}
                </div>
              );
          }
        })}
      </div>
    );
  };
  
  if (isLoading) {
    return (
      <div className="p-6">
        <ContentLoader message="Loading integration details..." size="lg" />
      </div>
    );
  }
  
  if (error || !product || !organization) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <AlertCircle size={48} className="text-status-danger mb-4" />
            <h3 className="text-xl font-medium mb-2">Error Loading Information</h3>
            <p className="text-text-muted-dark text-center max-w-md mb-6">
              {error || "Required information could not be loaded."}
            </p>
            <button 
              onClick={() => navigate('/integrations')} 
              className="premium-button-outline flex items-center"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Integrations
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      {/* Header section with gradient background */}
      <div className="mb-6 rounded-lg overflow-hidden relative z-10">
        <div className="bg-gradient-to-b from-gray-900/90 via-indigo-900/20 to-transparent p-6 rounded-xl backdrop-blur-sm border border-indigo-800/20 shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/integrations')} 
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/20 px-4 py-2 rounded-lg flex items-center transition-all duration-200 mr-4"
              >
                <ArrowLeft size={16} className="mr-1" />
                Back
              </button>
              <div>
                <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">{product.name} Integration</h1>
                <div className="flex items-center mt-1 text-gray-400">
                  <Building2 size={14} className="mr-1" />
                  <span>Configuring for {organization.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="card max-w-3xl mx-auto">
        <div className="flex items-center mb-6">
          {product.logo_url ? (
            <img 
              src={product.logo_url} 
              alt={product.name} 
              className="h-16 w-16 mr-4 object-contain bg-white rounded p-1"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '';
                (e.target as HTMLImageElement).classList.add('hidden');
              }} 
            />
          ) : (
            <div className="h-16 w-16 mr-4 flex items-center justify-center bg-background-hover rounded">
              <span className="text-lg font-bold text-text-muted-dark">
                {product.name.substring(0, 2).toUpperCase()}
              </span>
            </div>
          )}
        </div>
        
        {product.integration_fields?.length === 0 ? (
          <div className="bg-background-darker p-4 rounded-md mb-6">
            <p className="text-center text-text-muted-dark">
              This product doesn't require any integration settings.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {renderFormFields()}
            
            <div className="flex justify-end mt-8 pt-4 border-t border-border-dark">
              <button
                type="button"
                onClick={() => navigate('/integrations')}
                className="premium-button-outline mr-3"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="premium-button-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <Loader2 size={16} className="animate-spin mr-2" />
                    Saving...
                  </span>
                ) : existingIntegration ? (
                  <span className="flex items-center">
                    <Check size={16} className="mr-2" />
                    Update Integration
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Check size={16} className="mr-2" />
                    Save Integration
                  </span>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default IntegrateProductPage; 